import React, { useMemo } from 'react';
import { CircularProgress, MenuItem, Select } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useAllRestaurants } from '../../../../apis/shops-api';

type RestaurantSelectProps = {
  allowedShops: string[];
  selectedShopId?: string;
  // The drawer is on the overview of all the user's restaurants, so that is what the field reads.
  isOverviewSelected: boolean;
  onSelect: (shopExternalId: string) => void;
  onSelectAll: () => void;
};

const ROW_HEIGHT = 46;

// The option that is not a shop. A value no shop external id can take, so the two never collide.
const ALL_RESTAURANTS = '__all__';

/**
 * The shop picker, with the overview of every shop as its first option. The overview is the level above a
 * single shop, so it lives where the shop is chosen rather than as a page link beside one shop's pages.
 */
const RestaurantSelect: React.FC<RestaurantSelectProps> = ({
  allowedShops,
  selectedShopId,
  isOverviewSelected,
  onSelect,
  onSelectAll,
}) => {
  const { t } = useTranslation();

  // Only for the names: the users API gives the shops as bare ids.
  const { data: restaurants, isLoading: isRestaurantsLoading } = useAllRestaurants();
  const namesById = useMemo(
    () => new Map(restaurants?.map((shop) => [shop.shopExternalId, shop.shortName] as const)),
    [restaurants]
  );
  const shopName = (id: string) => namesById.get(id) ?? id;

  // An id outside the options (e.g. while the users API is still loading) would make MUI warn.
  const value = isOverviewSelected
    ? ALL_RESTAURANTS
    : selectedShopId && allowedShops.includes(selectedShopId)
    ? selectedShopId
    : '';

  const handleChange = (next: string) => {
    if (next === ALL_RESTAURANTS) {
      onSelectAll();
    } else if (next) {
      onSelect(next);
    }
  };

  return (
    <StyledSelect
      fullWidth
      displayEmpty
      value={value}
      onChange={(event) => handleChange(event.target.value)}
      endAdornment={isRestaurantsLoading ? <LoadingSpinner size={20} /> : undefined}
      renderValue={(id) =>
        id === ALL_RESTAURANTS ? t('nav.restaurant.all') : id ? shopName(id) : t('nav.restaurant.selectRestaurant')
      }
      // The menu renders in a portal, so it is sized here rather than in the styled field.
      MenuProps={{
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
        PaperProps: { style: { maxHeight: ROW_HEIGHT * 7, marginTop: 4 } },
        MenuListProps: { disablePadding: true },
      }}
    >
      {/* The hairline is the item's own bottom border: Select turns every child element into an option, so
          a Divider among the items would be read out as a blank one. */}
      <StyledMenuItem value={ALL_RESTAURANTS} divider>
        {t('nav.restaurant.all')}
      </StyledMenuItem>
      {allowedShops.map((id) => (
        <StyledMenuItem key={id} value={id}>
          {shopName(id)}
        </StyledMenuItem>
      ))}
    </StyledSelect>
  );
};

// Cast back, since styled() keeps only Select's last overload (the standard variant) and drops its value generic.
const StyledSelect = styled(Select)`
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  height: ${ROW_HEIGHT}px;
  font-size: ${({ theme }) => theme.fontSizes.sm};

  /* The input's 16.5px vertical padding would overflow the fixed height. */
  & .MuiSelect-select {
    padding-top: 0;
    padding-bottom: 0;
  }
` as typeof Select;

const StyledMenuItem = styled(MenuItem)`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  height: ${ROW_HEIGHT}px;
  min-height: ${ROW_HEIGHT}px;
`;

const LoadingSpinner = styled(CircularProgress)`
  margin-right: ${({ theme }) => theme.spacing(3)};
`;

export default RestaurantSelect;
